import { useEffect, useRef, useState } from 'react';
import { Loader } from 'lucide-react';

/**
 * LivePreview Component
 *
 * Renders HTML in a sandboxed iframe with click-to-navigate functionality.
 * Cmd/Ctrl+Click on any element sends navigation data to parent.
 */
const LivePreview = ({ html, onNavigate, isRendering }) => {
  const iframeRef = useRef(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Inject click navigation script into iframe
    const injectNavigationScript = () => {
      try {
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Check if script already injected
        if (iframeDoc.getElementById('nvm-code-nav-script')) return;

        const script = iframeDoc.createElement('script');
        script.id = 'nvm-code-nav-script';
        script.textContent = `
          (function() {
            // Prevent duplicate listeners
            if (window.__nvmNavigationInstalled) return;
            window.__nvmNavigationInstalled = true;

            window.addEventListener('click', function(e) {
              // Only trigger on Cmd (Mac) or Ctrl (Windows/Linux) click
              if (!(e.metaKey || e.ctrlKey)) return;

              e.preventDefault();
              e.stopPropagation();

              // Walk up to find meaningful element
              const meaningfulTags = [
                'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
                'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'NAV',
                'TABLE', 'UL', 'OL', 'LI', 'FORM', 'BLOCKQUOTE',
                'PRE', 'CODE', 'FIGURE', 'MAIN', 'ASIDE'
              ];

              let element = e.target;
              let depth = 0;
              const maxDepth = 10;

              while (element && depth < maxDepth) {
                if (meaningfulTags.includes(element.tagName)) {
                  break;
                }
                element = element.parentElement;
                depth++;
              }

              if (!element) return;

              // Extract element data
              const data = {
                id: element.id || '',
                className: element.className || '',
                tagName: element.tagName.toLowerCase(),
                textContent: (element.textContent || '').trim().slice(0, 50)
              };

              // Send message to parent window
              window.parent.postMessage({
                type: 'navigate-to-code',
                data: data
              }, '*');

              // Visual feedback - flash the element
              const originalBackground = element.style.backgroundColor;
              const originalTransition = element.style.transition;
              element.style.transition = 'background-color 0.3s';
              element.style.backgroundColor = 'rgba(239, 68, 68, 0.2)';

              setTimeout(() => {
                element.style.backgroundColor = originalBackground;
                setTimeout(() => {
                  element.style.transition = originalTransition;
                }, 300);
              }, 300);
            }, true);

            // Add helper text
            const style = document.createElement('style');
            style.textContent = \`
              body::before {
                content: 'Cmd+Click (Mac) or Ctrl+Click (Windows) on any element to jump to code';
                position: fixed;
                bottom: 10px;
                right: 10px;
                background: rgba(0, 0, 0, 0.8);
                color: white;
                padding: 8px 12px;
                border-radius: 4px;
                font-size: 12px;
                font-family: system-ui, -apple-system, sans-serif;
                z-index: 9999;
                pointer-events: none;
                opacity: 0;
                transition: opacity 0.3s;
              }

              body:hover::before {
                opacity: 1;
              }
            \`;
            document.head.appendChild(style);
          })();
        `;

        iframeDoc.head.appendChild(script);
        setError(null);
      } catch (err) {
        console.error('Failed to inject navigation script:', err);
        setError('Failed to initialize preview navigation');
      }
    };

    // Wait for iframe to load
    const handleLoad = () => {
      injectNavigationScript();
    };

    iframe.addEventListener('load', handleLoad);

    // Inject script if already loaded
    if (iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      injectNavigationScript();
    }

    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, []);

  // Listen for navigation messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: verify message origin if needed
      if (event.data && event.data.type === 'navigate-to-code') {
        onNavigate(event.data.data);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onNavigate]);

  // Prepare iframe content with proper HTML structure
  const iframeContent = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Preview</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
                         'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            padding: 20px;
            background: #fff;
          }

          img {
            max-width: 100%;
            height: auto;
          }

          h1, h2, h3, h4, h5, h6 {
            margin-top: 1em;
            margin-bottom: 0.5em;
          }

          p {
            margin-bottom: 1em;
          }

          ul, ol {
            margin-left: 2em;
            margin-bottom: 1em;
          }

          a {
            color: #dc2626;
            text-decoration: underline;
          }

          table {
            border-collapse: collapse;
            width: 100%;
            margin-bottom: 1em;
          }

          table td, table th {
            border: 1px solid #ddd;
            padding: 8px;
          }

          table th {
            background-color: #f3f4f6;
          }
        </style>
      </head>
      <body>
        ${html}
      </body>
    </html>
  `;

  return (
    <div className="relative w-full h-full bg-background border rounded">
      {isRendering && (
        <div className="absolute inset-0 flex items-center justify-center bg-background bg-opacity-80 z-10">
          <div className="flex items-center space-x-2 text-gray-600 dark:text-gray-400">
            <Loader className="h-5 w-5 animate-spin" />
            <span>Rendering preview...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="absolute top-4 left-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded z-10">
          {error}
        </div>
      )}

      <iframe
        ref={iframeRef}
        srcDoc={iframeContent}
        sandbox="allow-same-origin allow-scripts"
        className="w-full h-full border-0"
        title="HTML Preview"
      />
    </div>
  );
};

export default LivePreview;
